import * as React from 'react';
import PropTypes from 'prop-types';
import { useInstance } from '../logging/index.js';
import { LoadingTimeoutError } from './errors.js';
import * as errors from './errors.js';
export { errors as Errors };
import { SeonContext } from './context.js';

/**
 * Supported CDN hosts
 *
 * @enum {string}
 * @readonly
 *
 * @access public
 */

var CdnHost = {
  Deviceinf: "deviceinf.com",
  Seondf: "dfsdk.com",
  Seonintelligence: "seonintelligence.com"
};
/**
 * Supported DNS resolver domains
 *
 * @enum {string}
 * @readonly
 *
 * @access private
 */

var DnsResolverDomain = {
  Deviceinf: "deviceinfresolver.com",
  Getdeviceinf: "getdeviceinfresolver.com",
  Seonintelligence: "seonintelligenceresolver.com",
  Seondf: "seondfresolver.com",
  Seon: "seon.io"
};
/**
 * Create promise that can be resolved/rejected from outside its body
 *
 * @access private
 */

function usePromise() {
  return React.useMemo(() => {
    var outerReject;
    var outerResolve;
    var promise = new Promise((resolve, reject) => {
      outerReject = reject;
      outerResolve = resolve;
    });
    return [promise, outerReject, outerResolve];
  }, []);
}
/**
 * React Context provider for SEON SDK
 *
 * SEON SDK source is hosted on multiple CDNs to avoid browsers and extensions
 * that block trackers. If SEON SDK fails to load, try another `host`.
 *
 * @param {Object} props Component props
 * @param {number} props.attempts Retry attempts number
 * @param {boolean} props.disabled Disables SEON integration
 * @param {CdnHost} props.host CDN host to use
 * @param {RenderScriptCallback} props.renderScript See example
 * @param {number} props.timeout Retry timeout
 *
 * @example // Basic usage with React Helmet
 * // Herz does not manage <head>; <script> tag injection is up to consumer
 * <Herz.Seon.FingerprintProvider
 *   host="deviceinf.com"
 *   renderScript={(props) => (
 *     <Helmet>
 *       <script {...props} />
 *     </Helmet>
 *   )}
 * />
 *
 * @example // Usage with feature flags
 * // This will not wrap children with context provider and force SEON hooks
 * // to use no-op methods.
 * const isSeonEnabled = useFeatureFlag("SEON")
 *
 * <Herz.Seon.FingerprintProvider
 *   disabled={!isSeonEnabled}
 *   host="deviceinf.com"
 *   renderScript={renderScript}
 * />
 *
 * @access public
 */


function FingerprintProvider(props) {
  // SEON cannot run on server-side
  if (!process.browser) {
    return props.children;
  }

  var logger = useInstance();
  var [host, setHost] = React.useState(props.host);
  var [initialized, setInitialized] = React.useState(false);
  var [fingerprint, rejectFingerprint, resolveFingerprint] = usePromise();
  var value = React.useMemo(() => ({
    resolveFingerprint() {
      logger.trace("[seon] Resolving fingerprint...");
      return fingerprint;
    }

  }), [fingerprint]);
  React.useEffect(() => {
    var id;
    var attempts = props.attempts;
    var availableHosts = Object.values(CdnHost);
    var currentHost = props.host;

    function initialize() {
      if (window.seon) {
        var _window$seon, _window$seon$init;

        (_window$seon = window.seon) === null || _window$seon === void 0 ? void 0 : (_window$seon$init = _window$seon.init) === null || _window$seon$init === void 0 ? void 0 : _window$seon$init.call(_window$seon);
        setInitialized(true);
        logger.debug("[seon] Initialized with host:", host);
        return;
      }

      logger.debug("[seon] Initializing with host:", currentHost);
      setHost(currentHost);
      attempts -= 1;
      availableHosts = availableHosts.filter(host => host !== currentHost);

      if (attempts <= 0) {
        currentHost = availableHosts[0] || "null";

        if (currentHost === "null") {
          logger.debug("[seon] Failed to load SEON SDK from all hosts");
          rejectFingerprint(new LoadingTimeoutError());
          return;
        } // Try different CDN hosts and reset attempts


        attempts = props.attempts;
      }

      id = setTimeout(initialize, props.timeout);
      return;
    }

    initialize();
    return () => {
      logger.trace("[seon] Initializing cancelled");
      clearTimeout(id);
    };
  }, [logger, props.attempts, props.host, props.timeout, rejectFingerprint]);
  React.useEffect(() => {
    if (initialized) {
      logger.debug("[seon] Initialized, fetching session data...");
      var dnsResolverMap = {
        [CdnHost.Deviceinf]: DnsResolverDomain.Deviceinf,
        [CdnHost.Seondf]: DnsResolverDomain.Seondf,
        [CdnHost.Seonintelligence]: DnsResolverDomain.Seonintelligence
      };
      var data = window.seon.getSession({
        networkTimeoutMs: 2000,
        fieldTimeoutMs: 2000,
        dnsResolverDomain: dnsResolverMap[host]
      });
      resolveFingerprint(data);
    }
  }, [initialized, rejectFingerprint, resolveFingerprint]);

  if (props.disabled) {
    return props.children;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, props.renderScript({
    src: "https://cdn.".concat(host, "/js/v6/agent.umd.js"),
    defer: true
  }), /*#__PURE__*/React.createElement(SeonContext.Provider, {
    value: value
  }, props.children));
}
FingerprintProvider.defaultProps = {
  attempts: 20,
  host: CdnHost.Deviceinf,
  timeout: 250
};
FingerprintProvider.displayName = "Seon.FingerprintProvider";
FingerprintProvider.propTypes = {
  attempts: PropTypes.number,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  host: PropTypes.oneOf(Object.values(CdnHost)),

  /**
   * React Helmet workaround; see provider usage example
   */
  renderScript: PropTypes.func.isRequired,
  timeout: PropTypes.number
};
/**
 * Retrieve device fingerprint from SEON
 *
 * @access public
 */

function useFingerprint() {
  var context = React.useContext(SeonContext);
  var [error, setError] = React.useState();
  var [fetching, setFetching] = React.useState(true);
  var [value, setValue] = React.useState();
  var rawValue = React.useMemo(() => context.resolveFingerprint(), [context]);
  React.useEffect(() => {
    rawValue.then(value => {
      setFetching(false);
      setValue(value);
      return value;
    }).catch(error => {
      setFetching(false);
      setError(error);
    });
  }, [rawValue]);
  return {
    /**
     * Error object if operation failed
     *
     * @type {(Error|undefined)}
     */
    error,

    /**
     * Operation state
     *
     * @type {boolean}
     */
    fetching,

    /**
     * Original promise resolving to fingerprint
     *
     * @type {Promise<string>}
     */
    rawValue,

    /**
     * Fingerprint or undefined if fetching or unable to fetch
     *
     * @type {string}
     */
    value
  };
}

export { CdnHost, DnsResolverDomain, FingerprintProvider, useFingerprint };
